import React, { useEffect, useState } from 'react';
import { pageTitle } from '../../helper';
import PageHeading from '../PageHeading';
import Div from '../Div';
import SectionHeading from '../SectionHeading';
import Spacing from '../Spacing';
import { Helmet } from 'react-helmet';
import { bookingsAPI } from '../../helper/bookingsAPI';
import './BookingEditor.css';

export default function BookingEditor() {
  pageTitle('Editor rezervací');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null); // Store date being deleted
  const [newlySaved, setNewlySaved] = useState([]); // Store newly saved dates for animation
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [description, setDescription] = useState('Recording');

  useEffect(() => {
    window.scrollTo(0, 0);
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      const data = await bookingsAPI.getBookings();
      setBookings(data.bookings || []);
    } catch (error) {
      console.error('Chyba při načítání rezervací:', error);
      setBookings([]);
      // Removed alert - just log error silently
    } finally {
      setLoading(false);
    }
  };

  const getMonthDates = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the day of week for first day (0 = Sunday, 1 = Monday, etc.)
    // Convert to Monday = 0, Tuesday = 1, etc.
    const firstDayOfWeek = (firstDay.getDay() + 6) % 7;
    
    const dates = [];
    
    // Add padding days from previous month
    const prevMonth = new Date(year, month - 1, 0); // Last day of previous month
    const prevMonthLastDay = prevMonth.getDate();
    
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const date = new Date(year, month - 1, day);
      dates.push({ date, isCurrentMonth: false });
    }
    
    // Add all days of current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      dates.push({ date, isCurrentMonth: true });
    }
    
    // Add padding days from next month to complete the last week
    const remainingDays = 42 - dates.length; // 6 rows × 7 days = 42
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      dates.push({ date, isCurrentMonth: false });
    }
    
    return dates;
  };

  const formatDate = (date) => {
    // Místo toISOString() použijeme lokální datum
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('cs-CZ', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const getBookingForDate = (date) => {
    const dateStr = formatDate(date);
    return bookings.find(booking => booking.date === dateStr);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newDate);
    // Clear selection when changing months
    setSelectedDates([]);
    setStartDate(null);
    setSelectionMode(false);
  };

  const isDateSelected = (date) => {
    const dateStr = formatDate(date);
    return selectedDates.some(selectedDate => formatDate(selectedDate) === dateStr);
  };

  const isDateInRange = (date, start, end) => {
    if (!start || !end) return false;
    const checkDate = new Date(date);
    const startTime = start.getTime();
    const endTime = end.getTime();
    const checkTime = checkDate.getTime();
    return checkTime >= startTime && checkTime <= endTime;
  };

  const getDatesBetween = (startDate, endDate) => {
    const dates = [];
    const current = new Date(startDate);
    const end = new Date(endDate);
    
    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return dates;
  };

  const handleDateClick = (date, isCurrentMonth) => {
    if (!isCurrentMonth) return;

    const booking = getBookingForDate(date);
    if (booking) return; // Can't select already booked dates

    if (!selectionMode) {
      // Start new selection
      setStartDate(date);
      setSelectedDates([date]);
      setSelectionMode(true);
    } else {
      // Complete selection range
      if (startDate) {
        const start = startDate < date ? startDate : date;
        const end = startDate < date ? date : startDate;
        
        // Check if any dates in range are already booked
        const datesInRange = getDatesBetween(start, end);
        const hasBookedDates = datesInRange.some(d => getBookingForDate(d));
        
        if (hasBookedDates) {
          // Just clear selection, no alert
          clearSelection();
          return;
        }
        
        setSelectedDates(datesInRange);
      }
    }
  };

  const clearSelection = () => {
    setSelectedDates([]);
    setStartDate(null);
    setSelectionMode(false);
  };

  const saveBookings = async () => {
    if (selectedDates.length === 0) return;

    setSaving(true);
    const newBookings = selectedDates.map(date => ({
      date: formatDate(date),
      description: description
    }));

    try {
      await bookingsAPI.addBookings(newBookings);
      
      // Store newly saved dates for animation
      const savedDateStrings = newBookings.map(b => b.date);
      setNewlySaved(savedDateStrings);
      
      // Reload bookings from server to get updated data
      await loadBookings();
      
      // Clear selection
      clearSelection();
      
      // Clear animation state after delay
      setTimeout(() => {
        setNewlySaved([]);
      }, 800);
    } catch (error) {
      console.error('Chyba při ukládání rezervací:', error);
    } finally {
      setSaving(false);
    }
  };

  const deleteBooking = async (date) => {
    const dateStr = formatDate(date);
    setDeleting(dateStr);
    
    try {
      // Wait a bit for animation to start
      await new Promise(resolve => setTimeout(resolve, 100));
      
      await bookingsAPI.deleteBooking(dateStr);
      
      // Wait for animation to complete before reloading
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload bookings from server to get updated data
      await loadBookings();
    } catch (error) {
      console.error('Chyba při mazání rezervace:', error);
    } finally {
      setDeleting(null);
    }
  };

  const dayNames = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
  const monthDates = getMonthDates(currentMonth);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Editor rezervací - Stable Records</title>
          <link rel="icon" href="/stablerecords.ico" />
          <meta name="description" content="Editor rezervací nahrávacího studia Stable Records" />
        </Helmet>
        <PageHeading 
          title='Editor rezervací'
          bgSrc='images/service_hero_bg.jpeg'
          pageLinkText='Editor'
        />
        <Spacing lg='150' md='80'/>
        <Div className="container">
          <div className="text-center">Načítání...</div>
        </Div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Editor rezervací - Stable Records</title>
        <link rel="icon" href="/stablerecords.ico" />
        <meta name="description" content="Editor rezervací nahrávacího studia Stable Records" />
      </Helmet>
      
      <PageHeading 
        title='Editor rezervací'
        bgSrc='images/service_hero_bg.jpeg'
        pageLinkText='Editor'
      />
      
      <Div className="container">
        <Div className="row">
          <Div className="col-12">
            <SectionHeading
              title='Správa rezervací studia' 
              subtitle='Vyberte více dnů v řadě pro vytvoření nových rezervací nebo upravte stávající.'
            />
            
            <Spacing lg='30' md='20'/>
            
            {/* Controls */}
            <div className="booking-controls">
              <div className="control-group">
                <label htmlFor="description">Popis rezervace:</label>
                <input
                  type="text"
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Recording"
                  className="form-control"
                />
              </div>
              
              <div className="control-buttons">
                {selectionMode && (
                  <div className="selection-info">
                    <span>Režim výběru aktivní</span>
                    <button className="btn btn-secondary" onClick={clearSelection}>
                      Zrušit výběr
                    </button>
                  </div>
                )}
                
                {selectedDates.length > 0 && (
                  <div className="selected-info">
                    <span>Vybráno dní: {selectedDates.length}</span>
                    <button 
                      className="btn btn-success" 
                      onClick={saveBookings}
                      disabled={saving}
                    >
                      {saving ? 'Ukládá se...' : 'Uložit rezervace'}
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <Spacing lg='30' md='20'/>
            
            {/* Month Navigation */}
            <div className="calendar-navigation">
              <button 
                className="btn btn-outline-primary"
                onClick={() => navigateMonth(-1)}
              >
                ← Předchozí měsíc
              </button>
              <span className="current-month">
                {formatMonthYear(currentMonth)}
              </span>
              <button 
                className="btn btn-outline-primary"
                onClick={() => navigateMonth(1)}
              >
                Následující měsíc →
              </button>
            </div>
            
            <Spacing lg='30' md='20'/>
            
            {/* Calendar Grid */}
            <div className="calendar-container">
              <div className="calendar-grid">
                {/* Header with day names */}
                <div className="calendar-header">
                  {dayNames.map((dayName) => (
                    <div key={dayName} className="day-header">
                      {dayName}
                    </div>
                  ))}
                </div>
                
                {/* Calendar days */}
                {monthDates.map((dateObj, index) => {
                  const booking = getBookingForDate(dateObj.date);
                  const isBooked = !!booking;
                  const isSelected = isDateSelected(dateObj.date);
                  const isNewlySaved = newlySaved.includes(formatDate(dateObj.date));
                  const isInPreviewRange = selectionMode && startDate && 
                    isDateInRange(dateObj.date, 
                      startDate < dateObj.date ? startDate : dateObj.date, 
                      startDate < dateObj.date ? dateObj.date : startDate
                    );
                  
                  return (
                    <div 
                      key={index} 
                      className={`
                        calendar-cell 
                        ${isBooked ? 'occupied' : 'free'} 
                        ${!dateObj.isCurrentMonth ? 'other-month' : ''}
                        ${isSelected ? 'selected' : ''}
                        ${isInPreviewRange && !isBooked ? 'preview-range' : ''}
                        ${dateObj.isCurrentMonth && !isBooked ? 'selectable' : ''}
                        ${deleting === formatDate(dateObj.date) ? 'deleting' : ''}
                        ${isNewlySaved ? 'newly-saved' : ''}
                      `}
                      onClick={() => handleDateClick(dateObj.date, dateObj.isCurrentMonth)}
                      title={booking ? booking.description : 'Klikněte pro výběr'}
                    >
                      <div className="day-number">
                        {dateObj.date.getDate()}
                      </div>
                      
                      {isBooked && booking && (
                        <div className="booking-info">
                          <div className="booking-description">
                            {booking.description}
                          </div>
                          <button 
                            className="delete-booking-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteBooking(dateObj.date);
                            }}
                            title="Smazat rezervaci"
                            disabled={deleting === formatDate(dateObj.date)}
                          >
                            {deleting === formatDate(dateObj.date) ? '...' : '×'}
                          </button>
                        </div>
                      )}
                      
                      {!isBooked && dateObj.isCurrentMonth && !isSelected && (
                        <span className="free-text">Volné</span>
                      )}
                      
                      {isSelected && (
                        <span className="selected-text">Vybráno</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            
            <Spacing lg='50' md='30'/>
            
            {/* Legend */}
            <div className="calendar-legend">
              <div className="legend-item">
                <div className="legend-color occupied"></div>
                <span>Obsazeno</span>
              </div>
              <div className="legend-item">
                <div className="legend-color free"></div>
                <span>Volné</span>
              </div>
              <div className="legend-item">
                <div className="legend-color selected"></div>
                <span>Vybráno</span>
              </div>
              {selectionMode && (
                <div className="legend-item">
                  <div className="legend-color preview-range"></div>
                  <span>Náhled rozsahu</span>
                </div>
              )}
            </div>
            
            <Spacing lg='30' md='20'/>
            
            {/* Instructions */}
            <div className="instructions">
              <h5>Návod k použití:</h5>
              <ul>
                <li>Klikněte na první den pro začátek výběru</li>
                <li>Klikněte na poslední den pro dokončení rozsahu</li>
                <li>Vybrané dny budou označeny modrou barvou</li>
                <li>Pro zrušení výběru použijte tlačítko "Zrušit výběr"</li>
                <li>Pro smazání rezervace klikněte na × v obsazeném dni</li>
                <li>Nelze vybrat dny, které jsou již rezervované</li>
                <li>Změny se ukládají automaticky bez potvrzení</li>
              </ul>
            </div>
            
          </Div>
        </Div>
      </Div>
      
      <Spacing lg='150' md='80'/>
    </>
  );
}